import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyles from '@mui/material/GlobalStyles';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PrintIcon from '@mui/icons-material/Print';

import {
  runCohortReport,
  type CohortColumn,
  type CohortProcedure,
  type CohortReport,
} from '../../api/cohortReport';
import { extractErrorMessage } from '../../api/errors';
import { listProcedures, type Procedure } from '../../api/procedures';
import { listSessionRoles, type SessionRole } from '../../api/sessionRoles';
import { prettyMetric } from '../../util/metricLabels';
import { useFormatDateTime } from '../../util/displayTimeZone';
import { LEGENDS, deviationFill, percentileFill } from './cohortColours';

/** Value as shown in a cell: minutes for duration, % for fractions. */
export function formatValue(col: Pick<CohortColumn, 'key' | 'unit'>, v: number | null | undefined): string {
  if (v == null) return '–';
  if (col.key === 'duration') return (v / 60).toFixed(1);
  if (col.unit === '%') return String(Math.round(v * 100));
  if (col.unit === 's') return String(Math.round(v));
  const a = Math.abs(v);
  if (a >= 100) return v.toFixed(0);
  if (a >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

function columnLabel(col: CohortColumn): string {
  if (col.key === 'duration') return 'Duration (min)';
  if (col.unit === '%') return `${prettyMetric(col.key)} (%)`;
  return prettyMetric(col.key);
}

function arrow(col: CohortColumn): string {
  if (col.colour === 'neutral') return '';
  return col.direction === 'lower' ? ' ↓' : ' ↑';
}

/** Within-person deviation as text: % for the ratio indices, ± for valence. */
function formatDeviation(key: string, d: number): string {
  if (key === 'valence') return `${d >= 0 ? '+' : ''}${d.toFixed(2)}`;
  const pct = Math.round((Math.exp(d) - 1) * 100);
  return `${pct >= 0 ? '+' : ''}${pct}%`;
}

const PRINT_STYLES = (
  <GlobalStyles
    styles={{
      '@media print': {
        'header, nav, .MuiDrawer-root, .no-print, main > .MuiToolbar-root': {
          display: 'none !important',
        },
        'html, body, #root, #root > div, main': {
          height: 'auto !important',
          overflow: 'visible !important',
        },
        main: { width: '100% !important' },
        '.cohort-section': { breakInside: 'avoid' },
        '.cohort-procedure': { breakBefore: 'page' },
        '*': { printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' },
      },
    }}
  />
);

function Legend() {
  const bar = (stops: string[]) => (
    <Box sx={{ width: 120, height: 10, borderRadius: 1, background: `linear-gradient(90deg, ${stops.join(', ')})` }} />
  );
  return (
    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ fontSize: 12, color: 'text.secondary' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <span>worse than peers</span>
        {bar(LEGENDS.peer)}
        <span>better than peers</span>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <span>lowest</span>
        {bar(LEGENDS.neutral)}
        <span>highest (no better/worse)</span>
      </Stack>
    </Stack>
  );
}

function Coverage({ report }: { report: CohortReport }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} className="cohort-section">
      <Typography variant="h6" gutterBottom>
        Coverage
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Procedure</TableCell>
            <TableCell align="right">People</TableCell>
            <TableCell align="right">Sessions used</TableCell>
            <TableCell align="right">Nothing recorded</TableCell>
            <TableCell align="right">Under {report.min_session_seconds} s</TableCell>
            <TableCell align="right">No EEG</TableCell>
            <TableCell align="right">No hand data</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.procedures.map((p) => (
            <TableRow key={p.procedure_id}>
              <TableCell>{p.name}</TableCell>
              <TableCell align="right">{p.coverage.people}</TableCell>
              <TableCell align="right">
                {p.coverage.used} of {p.coverage.session_rows}
              </TableCell>
              <TableCell align="right">{p.coverage.empty}</TableCell>
              <TableCell align="right">{p.coverage.short}</TableCell>
              <TableCell align="right">{p.coverage.no_eeg}</TableCell>
              <TableCell align="right">{p.coverage.no_hand}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary">
        Sessions with nothing recorded or under {report.min_session_seconds} s are left out. Hand
        metrics are left out of sessions in which the wrist sensors recorded no activity.
      </Typography>
    </Paper>
  );
}

function quantile(sorted: number[], q: number): number {
  const i = (sorted.length - 1) * q;
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}

/** One row of dots per procedure: each person's median case length. */
function DurationSpread({ report }: { report: CohortReport }) {
  const series = report.procedures.map((p) => ({
    name: p.name,
    mins: p.rows
      .map((r) => r.values.duration)
      .filter((v): v is number => v != null)
      .map((v) => v / 60)
      .sort((a, b) => a - b),
  }));
  const max = Math.max(1, ...series.flatMap((s) => s.mins));
  const W = 720;
  const L = 130;
  const R = 150;
  const rowH = 34;
  const H = series.length * rowH + 28;
  const x = (m: number) => L + (m / max) * (W - L - R);
  const ticks = Array.from({ length: Math.floor(max) + 1 }, (_, i) => i).filter(
    (t) => max <= 10 || t % 2 === 0,
  );
  return (
    <Paper variant="outlined" sx={{ p: 2 }} className="cohort-section">
      <Typography variant="h6">How long did each person take?</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        One dot per person (the median of their sessions); the bar is the median, the shaded box the
        middle half.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <svg width={W} height={H} role="img" aria-label="Case length per procedure">
          {ticks.map((t) => (
            <g key={t}>
              <line x1={x(t)} x2={x(t)} y1={4} y2={H - 22} stroke="#e5e7eb" />
              <text x={x(t)} y={H - 8} fontSize={10} textAnchor="middle" fill="#6b7280">
                {t}
              </text>
            </g>
          ))}
          {series.map((s, i) => {
            const cy = 18 + i * rowH;
            if (s.mins.length === 0) return null;
            const q1 = quantile(s.mins, 0.25);
            const med = quantile(s.mins, 0.5);
            const q3 = quantile(s.mins, 0.75);
            return (
              <g key={s.name}>
                <text x={L - 8} y={cy + 4} fontSize={11} textAnchor="end" fill="#374151">
                  {s.name}
                </text>
                <rect x={x(q1)} y={cy - 9} width={Math.max(1, x(q3) - x(q1))} height={18} fill="#dbe7f5" rx={2} />
                {s.mins.map((m, j) => (
                  <circle
                    key={j}
                    cx={x(m)}
                    cy={cy + (((j * 7) % 11) - 5) * 1.3}
                    r={3}
                    fill="#2563a8"
                    stroke="#ffffff"
                    strokeWidth={1}
                  />
                ))}
                <line x1={x(med)} x2={x(med)} y1={cy - 11} y2={cy + 11} stroke="#1f2733" strokeWidth={2} />
                <text x={W - R + 10} y={cy + 4} fontSize={10} fill="#6b7280">
                  median {med.toFixed(1)} · n={s.mins.length}
                </text>
              </g>
            );
          })}
          <text x={(L + W - R) / 2} y={H} fontSize={10} textAnchor="middle" fill="#6b7280">
            minutes
          </text>
        </svg>
      </Box>
    </Paper>
  );
}

function ProcedureGrid({ proc }: { proc: CohortProcedure }) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [desc, setDesc] = useState(false);
  const rows = useMemo(() => {
    if (!sortKey) return proc.rows;
    const out = [...proc.rows];
    out.sort((a, b) => {
      const va = a.values[sortKey];
      const vb = b.values[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      return desc ? vb - va : va - vb;
    });
    return out;
  }, [proc.rows, sortKey, desc]);
  const onSort = (key: string) => {
    if (sortKey === key) setDesc(!desc);
    else {
      setSortKey(key);
      setDesc(false);
    }
  };
  const groups: { label: string; cols: CohortColumn[] }[] = [
    { label: 'Time', cols: proc.columns.filter((c) => c.modality === 'both') },
    { label: 'Brain (EEG)', cols: proc.columns.filter((c) => c.modality === 'eeg') },
    { label: 'Hand motion', cols: proc.columns.filter((c) => c.modality === 'hand') },
  ].filter((g) => g.cols.length > 0);
  const cols = groups.flatMap((g) => g.cols);

  return (
    <Paper variant="outlined" sx={{ p: 2 }} className="cohort-procedure">
      <Typography variant="h6">
        {proc.name}: {proc.rows.length} {proc.rows.length === 1 ? 'participant' : 'participants'} side by side
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        One row per participant, shortest case first (click a column to sort). The colour places each
        value among the people who did this procedure.
      </Typography>
      {proc.rows.length === 0 ? (
        <Typography variant="body2">No sessions in this range.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.25, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell />
                {groups.map((g) => (
                  <TableCell key={g.label} colSpan={g.cols.length} sx={{ fontWeight: 600 }}>
                    {g.label}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Participant</TableCell>
                {cols.map((c) => (
                  <TableCell key={c.key} align="center" sx={{ minWidth: 64, verticalAlign: 'bottom' }}>
                    <TableSortLabel
                      active={sortKey === c.key}
                      direction={sortKey === c.key && desc ? 'desc' : 'asc'}
                      onClick={() => onSort(c.key)}
                    >
                      {columnLabel(c)}
                      {arrow(c)}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.participant_user_id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.name}
                    {r.sessions > 1 && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}
                        ({r.sessions})
                      </Typography>
                    )}
                  </TableCell>
                  {cols.map((c) => {
                    const p = r.percentiles[c.key];
                    const paint = percentileFill(p, c.colour);
                    return (
                      <Tooltip
                        key={c.key}
                        title={p == null ? 'no data' : `${Math.round(p)}th percentile among peers`}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            ...(paint ?? {}),
                            border: '2px solid',
                            borderColor: 'background.paper',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatValue(c, r.values[c.key])}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {proc.columns.some((c) => c.colour === 'adjusted') && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Smoothness: the value is the scorecard&apos;s; the colour ranks it with the effect of case
          length removed, because longer cases score rougher whatever the hands did.
        </Typography>
      )}
    </Paper>
  );
}

/** Each person's percentile at every station, for one metric. */
function StationProfile({ report }: { report: CohortReport }) {
  const candidates = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of report.procedures)
      for (const c of p.columns) if (c.modality !== 'eeg') counts.set(c.key, (counts.get(c.key) ?? 0) + 1);
    return [...counts].filter(([, n]) => n >= 2).map(([k]) => k);
  }, [report]);
  const [key, setKey] = useState<string>('duration');
  const metric = candidates.includes(key) ? key : candidates[0];
  if (report.procedures.length < 2 || !metric) return null;

  const people = new Map<string, { name: string; cells: Map<string, number | null> }>();
  for (const p of report.procedures) {
    for (const r of p.rows) {
      const entry = people.get(r.participant_user_id) ?? { name: r.name, cells: new Map() };
      entry.cells.set(p.procedure_id, r.percentiles[metric] ?? null);
      people.set(r.participant_user_id, entry);
    }
  }
  const rows = [...people.values()]
    .filter((e) => e.cells.size >= 2)
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Paper variant="outlined" sx={{ p: 2 }} className="cohort-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box>
          <Typography variant="h6">Across stations</Typography>
          <Typography variant="body2" color="text.secondary">
            Each person&apos;s percentile at every station they did — who is consistently quick, slow or
            steady.
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Metric"
          value={metric}
          onChange={(e) => setKey(e.target.value)}
          sx={{ minWidth: 200 }}
          className="no-print"
        >
          {candidates.map((k) => (
            <MenuItem key={k} value={k}>
              {k === 'duration' ? 'Duration' : prettyMetric(k)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Nobody did more than one of these stations.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto', mt: 1 }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.25, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Participant</TableCell>
                {report.procedures.map((p) => (
                  <TableCell key={p.procedure_id} align="center">
                    {p.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.name}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.name}</TableCell>
                  {report.procedures.map((p) => {
                    const col = p.columns.find((c) => c.key === metric);
                    const v = e.cells.get(p.procedure_id);
                    const paint = col ? percentileFill(v, col.colour) : null;
                    return (
                      <TableCell
                        key={p.procedure_id}
                        align="center"
                        sx={{ ...(paint ?? {}), border: '2px solid', borderColor: 'background.paper' }}
                      >
                        {v == null ? '–' : Math.round(v)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

/** EEG relative to each person's own average over their stations. */
function WithinPersonEegView({ report }: { report: CohortReport }) {
  const [key, setKey] = useState<string>('');
  const metric = report.within_person_keys.includes(key) ? key : report.within_person_keys[0];
  if (!metric || report.within_person_eeg.length === 0) return null;
  const rows = report.within_person_eeg.filter((w) =>
    Object.values(w.stations).some((s) => s[metric] != null),
  );
  const bar = `linear-gradient(90deg, ${LEGENDS.within.join(', ')})`;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} className="cohort-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box>
          <Typography variant="h6">Brain (EEG): which station stood out for each person</Typography>
          <Typography variant="body2" color="text.secondary">
            EEG levels differ more between people (and how the headband sits) than between stations,
            so they are not ranked between people. Instead each station is compared with the
            person&apos;s own average over the stations they did.
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="EEG index"
          value={metric}
          onChange={(e) => setKey(e.target.value)}
          sx={{ minWidth: 200 }}
          className="no-print"
        >
          {report.within_person_keys.map((k) => (
            <MenuItem key={k} value={k}>
              {prettyMetric(k)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ fontSize: 12, color: 'text.secondary', my: 1 }}>
        <span>lower than usual for this person</span>
        <Box sx={{ width: 120, height: 10, borderRadius: 1, background: bar }} />
        <span>higher than usual</span>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.25, fontSize: 12 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Participant</TableCell>
              {report.procedures.map((p) => (
                <TableCell key={p.procedure_id} align="center">
                  {p.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w.participant_user_id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{w.name}</TableCell>
                {report.procedures.map((p) => {
                  const d = w.stations[p.procedure_id]?.[metric];
                  const paint = deviationFill(d);
                  return (
                    <TableCell
                      key={p.procedure_id}
                      align="center"
                      sx={{ ...(paint ?? {}), border: '2px solid', borderColor: 'background.paper' }}
                    >
                      {d == null ? '–' : formatDeviation(metric, d)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

/**
 * Analysis → Reports (COHORT-REPORT): pick procedures, a date range and
 * optionally a role; see the participants side by side. Admins and
 * coordinators only — the grid shows everyone's names.
 */
export function CohortReportView({ tenantId }: { tenantId: string }) {
  const formatDateTime = useFormatDateTime();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [roles, setRoles] = useState<SessionRole[]>([]);
  const [chosen, setChosen] = useState<Procedure[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [role, setRole] = useState('');
  const [report, setReport] = useState<CohortReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    listProcedures(tenantId)
      .then((ps) => live && setProcedures(ps))
      .catch((e) => live && setError(extractErrorMessage(e, 'Could not load procedures')));
    listSessionRoles(tenantId)
      .then((rs) => live && setRoles(rs))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [tenantId]);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(
        await runCohortReport(tenantId, {
          procedure_ids: chosen.map((p) => p.id),
          date_from: dateFrom || null,
          date_to: dateTo || null,
          role_name: role || null,
        }),
      );
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not build the report'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      {PRINT_STYLES}
      <Paper variant="outlined" sx={{ p: 2 }} className="no-print">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Autocomplete
            multiple
            options={procedures}
            value={chosen}
            onChange={(_e, v) => setChosen(v)}
            getOptionLabel={(p) => p.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Procedures" />}
            sx={{ flex: 1, minWidth: 260 }}
          />
          <TextField
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {roles.length > 0 && (
            <TextField
              select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All roles</MenuItem>
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.name}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button variant="contained" onClick={run} disabled={chosen.length === 0 || loading}>
            Build report
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {report && !loading && (
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5">
                {report.procedures.map((p) => p.name).join(', ')}: participants side by side
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {report.request.date_from || report.request.date_to
                  ? `Sessions ${report.request.date_from ?? '…'} to ${report.request.date_to ?? '…'}`
                  : 'All sessions'}
                {report.request.role_name ? ` · role ${report.request.role_name}` : ''} · built{' '}
                {formatDateTime(report.generated_at)}
              </Typography>
            </Box>
            <Button startIcon={<PrintIcon />} onClick={() => window.print()} className="no-print">
              Print / save as PDF
            </Button>
          </Stack>
          <Legend />
          <Coverage report={report} />
          <DurationSpread report={report} />
          <StationProfile report={report} />
          <WithinPersonEegView report={report} />
          {report.procedures.map((p) => (
            <ProcedureGrid key={p.procedure_id} proc={p} />
          ))}
          <Typography variant="caption" color="text.secondary">
            Each value is the median of the person&apos;s task-phase sessions; percentiles are among the
            people who did the procedure. Only metrics shown on this tenant&apos;s scorecard for each
            procedure are included. Duration and EEG are shaded without a better/worse verdict.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
